import { useLazyQuery, useMutation, useQuery } from "@apollo/client/react";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { type FormEvent, useEffect, useState } from "react";
import {
  ADD_TASK_FROM_OUTLOOK_MUTATION,
  GENERATE_TASK_NAME_QUERY,
  RAIN_ONE_LISTS_QUERY,
} from "../graphql/operations";

interface TaskResult {
  message: string;
  url?: string;
}

export default function CreateTaskForm() {
  const [taskName, setTaskName] = useState("");
  const [description, setDescription] = useState("");
  const [listKey, setListKey] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [assignToMyself, setAssignToMyself] = useState(true);
  const [taskResult, setTaskResult] = useState<TaskResult | null>(null);
  const [submitError, setSubmitError] = useState("");
  const [nameGenError, setNameGenError] = useState("");

  const { data: listsData, loading: listsLoading } =
    useQuery(RAIN_ONE_LISTS_QUERY);

  const [addTask, { loading: saving }] = useMutation(
    ADD_TASK_FROM_OUTLOOK_MUTATION,
  );

  const [generateTaskName, { loading: generatingName }] = useLazyQuery(GENERATE_TASK_NAME_QUERY);

  const mailItem = Office.context?.mailbox?.item;

  async function attemptGenerateTaskName(subject: string, body: string) {
    setNameGenError("");
    try {
      const { data, error } = await generateTaskName({
        variables: { emailSubject: subject, emailBody: body },
      });
      if (error) {
        setNameGenError(`Task name generation failed: ${error.message}`);
      } else {
        const suggestion = data?.generateTaskNameFromEmail;
        if (!suggestion) {
          setNameGenError("Task name generation returned no result");
        } else if (suggestion.confidence <= 0.6) {
          setNameGenError(`Task name suggestion confidence too low (${(suggestion.confidence * 100).toFixed(0)}%) — please enter a name manually`);
        } else {
          setTaskName(suggestion.taskName);
        }
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setNameGenError(`Task name generation error: ${msg}`);
    }
  }

  function handleRetryGenerateName() {
    const subject = mailItem?.subject ?? "";
    mailItem?.body.getAsync(Office.CoercionType.Text, (result) => {
      const body =
        result.status === Office.AsyncResultStatus.Succeeded
          ? result.value?.trim() ?? ""
          : "";
      attemptGenerateTaskName(subject, body);
    });
  }

  // Auto-fill description with subject + body from the current email
  useEffect(() => {
    if (!mailItem) return;

    const subject = mailItem.subject ?? "";

    mailItem.body.getAsync(Office.CoercionType.Text, async (result) => {
      const body =
        result.status === Office.AsyncResultStatus.Succeeded
          ? result.value?.trim() ?? ""
          : "";
      const desc = body ? `${subject}\n\n${body}` : subject;
      setDescription(desc);

      // Ask AI for a task name suggestion
      await attemptGenerateTaskName(subject, body);
    });
  }, [mailItem]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setTaskResult(null);
    setSubmitError("");

    // Build an Outlook deep link for the current email
    const outlookMessageUrl = await new Promise<string | undefined>(
      (resolve) => {
        // Try the synchronous itemId first (available in read mode)
        const syncId = mailItem?.itemId;
        if (syncId) {
          resolve(`https://outlook.office.com/mail/id/${encodeURIComponent(syncId)}`);
          return;
        }
        // Fall back to async API (compose mode or older hosts)
        if (mailItem?.getItemIdAsync) {
          mailItem.getItemIdAsync((result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded && result.value) {
              resolve(`https://outlook.office.com/mail/id/${encodeURIComponent(result.value)}`);
            } else {
              resolve(undefined);
            }
          });
        } else {
          resolve(undefined);
        }
      },
    );

    // Get the reply-to / from address
    const replyToEmailAddress = mailItem?.from?.emailAddress;

    let result;
    try {
      result = await addTask({
        variables: {
          input: {
            listKey,
            taskName,
            description,
            assignToMyself,
            outlookMessageUrl,
            replyToEmailAddress,
            dueDate: dueDate || undefined,
          },
        },
      });
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : "Failed to create task");
      return;
    }

    const payload = result.data?.addTaskFromOutlook;
    if (payload?.errors?.length) {
      const messages = payload.errors.map((e) => e.message).join(", ");
      setSubmitError(messages);
      return;
    }

    if (payload?.outlookTaskReply) {
      setTaskResult({
        message: payload.outlookTaskReply.messageMarkdown ?? "Task created successfully!",
        url: payload.outlookTaskReply.url ?? undefined,
      });
    }
  }

  function handleReplyAll() {
    if (!mailItem || !taskResult) return;

    mailItem.displayReplyAllForm({
      htmlBody: taskResult.url
        ? `<p>${taskResult.message}</p><p><a href="${taskResult.url}">${taskResult.url}</a></p>`
        : `<p>${taskResult.message}</p>`,
    });
  }

  function handleNewTask() {
    setTaskResult(null);
    setSubmitError("");
    setTaskName("");
    setDescription("");
    setListKey("");
    setDueDate("");
    setAssignToMyself(true);
  }

  const canSubmit = taskName.trim() && listKey && !saving;

  // --- Success view ---
  if (taskResult) {
    return (
      <Stack spacing={2.5}>
        <Alert severity="success" sx={{ alignItems: "flex-start" }}>
          <Typography variant="body2">{taskResult.message}</Typography>
          {taskResult.url && (
            <Link href={taskResult.url} target="_blank" rel="noopener" sx={{ mt: 0.5, display: "block", wordBreak: "break-all" }}>
              {taskResult.url}
            </Link>
          )}
        </Alert>
        <Stack direction="row" spacing={1.5} sx={{ justifyContent: "flex-end" }}>
          {mailItem && (
            <Button variant="outlined" onClick={handleReplyAll}>
              Reply All
            </Button>
          )}
        </Stack>
      </Stack>
    );
  }

  // --- Form view ---
  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack spacing={2.5}>
        {/* Task Name */}
        <TextField
          label="Task Name"
          placeholder={generatingName ? "Generating..." : "Enter a task name"}
          value={taskName}
          onChange={(e) => setTaskName(e.target.value)}
          required
          fullWidth
          size="small"
          autoFocus
          multiline
          minRows={2}
          maxRows={2}
          disabled={generatingName}
          slotProps={{
            input: {
              endAdornment: generatingName ? (
                <InputAdornment position="end">
                  <CircularProgress size={18} />
                </InputAdornment>
              ) : nameGenError ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleRetryGenerateName}
                    title="Retry task name generation"
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            },
          }}
        />

        {/* Description */}
        <TextField
          label="Description"
          placeholder="Add a description..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          minRows={3}
          maxRows={6}
          fullWidth
          size="small"
        />

        {/* RainONE List dropdown */}
        <TextField
          select
          label="RainONE List"
          placeholder="Select a list"
          value={listKey}
          onChange={(e) => setListKey(e.target.value)}
          required
          fullWidth
          size="small"
          disabled={listsLoading}
          slotProps={{
            input: {
              startAdornment: listsLoading ? (
                <CircularProgress size={16} sx={{ mr: 1 }} />
              ) : undefined,
            },
          }}
        >
          {(listsData?.rainOneLists ?? []).map((list) => (
            <MenuItem key={list.key} value={list.key}>
              {list.listName}
            </MenuItem>
          ))}
        </TextField>

        {/* Due Date */}
        <TextField
          label="Due Date"
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          fullWidth
          size="small"
          helperText="Optional"
          slotProps={{ inputLabel: { shrink: true } }}
        />

        {/* Assign to Myself */}
        <FormControlLabel
          control={
            <Checkbox
              checked={assignToMyself}
              onChange={(e) => setAssignToMyself(e.target.checked)}
              size="small"
            />
          }
          label="Assign to myself"
        />

        {/* Actions */}
        <Stack direction="row" spacing={1.5} sx={{ justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            onClick={handleNewTask}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!canSubmit}
            sx={{
              bgcolor: "#1B5E20",
              "&:hover": { bgcolor: "#2E7D32" },
            }}
          >
            {saving ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Create Task"
            )}
          </Button>
        </Stack>

        {/* Error display — below the Create Task button */}
        {submitError && (
          <Alert severity="error">{submitError}</Alert>
        )}
        {nameGenError && !submitError && (
          <Alert severity="warning">{nameGenError}</Alert>
        )}
      </Stack>
    </Box>
  );
}
